// app/CoordinatorElectionGate.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import { publishBootstrapFromLocalDb, unpublishBootstrap } from "@/lib/lanBootstrap";
import { fireAndForgetMesh, getMeshStatus, subscribeMesh } from "@/lib/lanMesh";
import { getDeviceId, getDeviceLabel, getInstalledPlatformTag, isInstalledAppEnvironment } from "@/lib/device";
import {
  claimCoordinator,
  clearAskedToday,
  clearClaimedToday,
  createHeartbeatLoop,
  evaluateCoordinatorElection,
  fetchCurrentCoordinator,
  fetchCurrentCoordinatorDetailed,
  markAskedToday,
  markClaimedToday,
  markHandoffCooldown,
  releaseCoordinator,
  skipCoordinatorElection,
  wasClaimedToday,
} from "@/lib/coordinatorRole";
import { configureCoordinatorMesh } from "@/lib/coordinatorMesh";
import { readRoleSession } from "@/lib/authStorage";



/**
 * CoordinatorElectionGate — daily election prompt (Session 27.53+).
 *
 * On every operational dashboard (staff / owner / kitchen / bar /
 * admin) and only in an installed context (Capacitor APK / Tauri EXE
 * / installed PWA), this component checks whether today's Coordinator
 * has been chosen. If not, it opens a modal on THIS device asking
 * "Is this device the Coordinator for today?" with Yes / Skip.
 *
 * - Yes → this device claims the role and starts the 30s heartbeat.
 * - Skip → the modal closes forever on this device today; the next
 *          device to log in gets the same prompt.
 *
 * Regular browser tabs never see the modal — coordinator is a POS-
 * network concept, not a browser concept.
 */
export function CoordinatorElectionGate() {
  const location = useLocation();
  // Detect whether the app is running inside a native shell / installed
  // PWA. `useState` snapshots the check once — we don't need to react
  // to install events mid-render because the coordinator role is
  // established at login time, not on install.
  const [isInstalledCtx] = useState(() => {
    try { return isInstalledAppEnvironment(); } catch (_) { return false; }
  });
  const [platformTag] = useState(() => {
    try { return getInstalledPlatformTag(); } catch (_) { return "unknown"; }
  });
  const [deviceId] = useState(() => {
    try { return getDeviceId(); } catch (_) { return ""; }
  });
  const [deviceLabel] = useState(() => {
    try { return getDeviceLabel(); } catch (_) { return "Device"; }
  });

  // Local state for the modal + role status.
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCoordinator, setIsCoordinator] = useState(false);
  // Session 27.63 — Hand-off dialog state. Confirms before releasing
  // the Coordinator role so a stray tap on the pill doesn't drop the
  // authoritative device mid-shift.
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  // Session 27.64 — Peer-visible coordinator health. When this device
  // is NOT the coordinator, we still show a small subtle pill in the
  // bottom-left corner listing "Coordinator: {label} · {age}" so ANY
  // staff/kitchen/bar member can glance at the pill and know whether
  // the LAN's authoritative writer is reachable. If the heartbeat age
  // exceeds ~90 seconds, the pill switches to a warning tint so the
  // team knows something's off before customers complain about
  // missing orders.
  const [peerCoordinator, setPeerCoordinator] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const heartbeatRef = useRef(null);

  // Which dashboard route are we on?
  const dashboardRouteMatch = /^\/(staff|owner|kitchen|bar|runner|admin)\/dashboard/.test(location.pathname);

  // Which session token is active for this route? We look at each of
  // the role-scoped sessionStorage keys and pick the one that matches
  // the current path. This avoids the gate trying to run before login
  // on `/staff`, `/owner`, etc.
  const activeToken = useMemo(() => {
    if (!dashboardRouteMatch) return null;
    const roleFromPath = location.pathname.split("/")[1];
    try {
      const parsed = readRoleSession(roleFromPath, "tab");
      return parsed && parsed.token ? parsed.token : null;
    } catch (_) {
      return null;
    }
  }, [dashboardRouteMatch, location.pathname]);

  // Kick off the election evaluation whenever we land on a dashboard
  // with a valid session, in an installed context. Guarded so we
  // don't run in a browser tab or without a token.
  //
  // Session 27.72 — Manual rescan bump. Incrementing `rescanTick`
  // re-runs this effect after clearing the "asked today" lock, so
  // the operator can force a fresh election check from the pill.
  const [rescanTick, setRescanTick] = useState(0);
  // Expose a global handle so the dashboard-header "Refresh" button
  // can also trigger a coordinator rescan without needing prop
  // plumbing across the entire tree. Cleaned up on unmount.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const bizId = (() => {
      try { return window.localStorage.getItem("nfcs.business_code") || null; }
      catch (_) { return null; }
    })();
    window.__nfcsRescanCoordinator = () => {
      try { clearAskedToday(deviceId, bizId); } catch (_) { /* noop */ }
      setRescanTick((n) => n + 1);
    };
    return () => {
      try { delete window.__nfcsRescanCoordinator; } catch (_) { /* noop */ }
    };
  }, [deviceId]);

  // Session 27.94 — Coordinator presence beacon over the mesh.
  //
  // The dashboard-role field on a mesh peer announcement ("staff",
  // "kitchen"…) doesn't distinguish which of them holds the
  // Coordinator role today, so my previous "look for a
  // role='coordinator' peer" check in Session 27.93 never matched.
  // Replace it with an explicit mesh beacon: while we hold the role,
  // broadcast `coordinator_beacon` every 12 s. Every peer stamps the
  // sender + timestamp into `lastSeenCoordinatorRef`. Election code
  // checks that ref first — if we've heard a beacon in the last 60 s
  // from a device that isn't us, there is definitively a live
  // coordinator on the LAN and we silent-continue without prompting.
  const lastSeenCoordinatorRef = useRef({ device_id: null, at: 0 });
  useEffect(() => {
    if (!isCoordinator || !deviceId) return undefined;
    const beacon = () => {
      try {
        fireAndForgetMesh({
          type: "coordinator_beacon",
          from_device_id: deviceId,
          at: new Date().toISOString(),
        }, { targetRole: "*" });
      } catch (_) { /* mesh best-effort */ }
    };
    // Fire once immediately so peers coming online right now don't
    // have to wait a full 12 s to learn we exist.
    beacon();
    const id = window.setInterval(beacon, 12000);
    return () => window.clearInterval(id);
  }, [isCoordinator, deviceId]);
  useEffect(() => {
    if (!deviceId) return undefined;
    const unsub = subscribeMesh((envelope) => {
      try {
        if (!envelope || envelope.type !== "coordinator_beacon") return;
        if (!envelope.from_device_id || envelope.from_device_id === deviceId) return;
        lastSeenCoordinatorRef.current = {
          device_id: envelope.from_device_id,
          at: Date.now(),
        };
      } catch (_) { /* noop */ }
    });
    return () => { try { unsub(); } catch (_) { /* noop */ } };
  }, [deviceId]);

  // Session 27.78 — Listen for coordinator_vacated broadcasts from
  // any peer that just picked "No, move to a different device". When
  // we receive one, clear our local "asked today" lock and re-run
  // evaluate — this is what makes the election modal cascade to the
  // next device instead of just quietly disappearing after the first
  // Skip. Without this, only the origin device knew the role was
  // available, so it would race-claim + release + re-prompt itself.
  useEffect(() => {
    if (!deviceId) return undefined;
    const unsub = subscribeMesh((envelope, _meta) => {
      try {
        if (!envelope || envelope.type !== "coordinator_vacated") return;
        if (envelope.from_device_id === deviceId) return; // ignore our own broadcast
        const bizId = (() => {
          try { return window.localStorage.getItem("nfcs.business_code") || null; }
          catch (_) { return null; }
        })();
        try { clearAskedToday(deviceId, bizId); } catch (_) { /* noop */ }
        setRescanTick((n) => n + 1);
      } catch (_) { /* noop */ }
    });
    return () => {
      try { unsub(); } catch (_) { /* noop */ }
    };
  }, [deviceId]);
  useEffect(() => {
    let cancelled = false;
    if (!isInstalledCtx || !dashboardRouteMatch || !activeToken || !deviceId) return undefined;

    // Session 27.96 — Eligibility gate.
    //
    // The Local Coordinator role now belongs EXCLUSIVELY to the
    // Kitchen or Bar dashboards. Staff, owner, manager, admin,
    // customer, and the Windows EXE never claim, never prompt, and
    // never appear as election candidates. Reasons this simplifies
    // everything:
    //   • Kitchen and Bar tablets sit in fixed locations all day
    //     with stable Wi-Fi — perfect for a coordinator's persistent
    //     heartbeat. Waiter tablets roam and lock, dropping the
    //     coordinator repeatedly.
    //   • Owners/managers may sign in from home over cloud only
    //     (no LAN peer visibility) — they can't be a viable
    //     coordinator anyway.
    //   • Eliminates the multi-device prompt cascade that operators
    //     have been complaining about for many sessions.
    // Session 30.1 — Eligibility + failover priority.
    //
    // Coordinator candidates in priority order (per operator spec):
    //   1. Kitchen  — fixed location, stable Wi-Fi, on all day.
    //   2. Bar      — same properties, second choice.
    //   3. Staff    — fallback ONLY (waiter tablets roam), so the
    //                 restaurant still has a coordinator when neither
    //                 kitchen nor bar devices are running.
    // Owner, manager, admin, runner, retail and browser tabs never
    // claim, never prompt.
    const currentDashboard = (location.pathname.split("/")[1] || "").toLowerCase();
    const COORDINATOR_ELIGIBLE_ROUTES = new Set(["kitchen", "bar", "staff"]);
    if (!COORDINATOR_ELIGIBLE_ROUTES.has(currentDashboard)) {
      // Non-eligible dashboards silently listen for the coordinator
      // beacon (so `configureCoordinatorMesh` still gets updated),
      // but they NEVER prompt.
      configureCoordinatorMesh({
        isCoordinator: false,
        coordinatorDeviceId: null,
        businessId: null,
        role: currentDashboard || null,
      });
      return undefined;
    }

    // Session 27.72 — Scope the "asked today" lock by business_id so
    // a device that switches businesses (rare) doesn't inherit
    // yesterday's decision. Falls back to "any" when the code isn't
    // in localStorage yet.
    let businessCodeForLock = null;
    try {
      businessCodeForLock = window.localStorage.getItem("nfcs.business_code") || null;
    } catch (_) { businessCodeForLock = null; }

    (async () => {
      // Session 27.93 — Wait for LAN mesh to stabilise before running
      // the election. This addresses two operator-reported bugs:
      //
      //   1. "The coordinator popup still appears even when a
      //      coordinator is already selected." When N tablets boot in
      //      the same second, each one calls `/coordinator/current`
      //      before ANY of them has claimed, sees no coordinator, and
      //      all N race into the auto-claim → release → prompt path
      //      simultaneously. The staggered stagger + 3.5 s discovery
      //      window inside `evaluateCoordinatorElection` reduces the
      //      race but doesn't eliminate it, especially when the
      //      backend read replica lags the primary during the burst.
      //
      //   2. "It needs to first make sure all the devices are on LAN
      //      and that it shows on all devices before asking for a
      //      coordinator role." — direct operator instruction. If a
      //      peer is already running the coordinator role on the LAN,
      //      the mesh will surface them within a few seconds; we
      //      should notice and silent-continue without pinging the
      //      backend at all.
      //
      // Strategy:
      //   a) Wait up to 8 s for the mesh status to reach "lan"
      //      (WebRTC handshakes complete with at least one peer).
      //      During the wait, if `peerCoordinatorRef` fires or the
      //      mesh reports a role="coordinator" peer, we silent-
      //      continue immediately.
      //   b) After the wait — regardless of whether mesh reached
      //      "lan" or not — compute a deterministic election
      //      candidate: the alphabetically-lowest peer_id among
      //      { self } ∪ mesh.announcedPeers. Only that candidate
      //      proceeds with `evaluateCoordinatorElection`. Every other
      //      device silent-continues, trusting the leader will claim
      //      within the next few seconds and their next
      //      `/coordinator/current` poll will pick that claim up.
      //
      // This turns a chaotic multi-way race into a coordinated
      // election with a single caller in the ideal case, while
      // remaining safe if the mesh never discovers peers (falls back
      // to the old behaviour of every device racing — but at least
      // the wait gives the fastest one a head start).
      const MESH_WAIT_MS = 8000;
      // Session 30.1 — Fast-resume path. If THIS device already
      // claimed the coordinator role today (localStorage per-day
      // memory), skip the mesh-stabilisation wait and the candidate
      // lottery entirely — `evaluateCoordinatorElection` will
      // silently reclaim (online) or resume locally (offline). This
      // is what makes switching Kitchen → Bar on the coordinator
      // device instant and prompt-free.
      const alreadyClaimedToday = (() => {
        try { return wasClaimedToday(deviceId, businessCodeForLock); } catch (_) { return false; }
      })();
      const meshDeadline = Date.now() + (alreadyClaimedToday ? 0 : MESH_WAIT_MS);
      let sawMeshCoordinator = null;
      while (Date.now() < meshDeadline) {
        if (cancelled) return;
        // Session 27.94 — Primary check: did any peer broadcast a
        // `coordinator_beacon` within the last 60 s? If so, trust
        // it. This is the reliable signal the previous Session 27.93
        // role-based check tried (and failed) to derive from the
        // mesh peer list.
        const beacon = lastSeenCoordinatorRef.current || { device_id: null, at: 0 };
        if (beacon.device_id && beacon.device_id !== deviceId
            && (Date.now() - beacon.at) < 60000) {
          sawMeshCoordinator = { peer_id: beacon.device_id };
          break;
        }
        let snap = null;
        try { snap = getMeshStatus(); } catch (_) { snap = null; }
        // Mesh has reached full LAN readiness — no need to wait longer.
        if (snap && snap.status === "lan" && (snap.peerCount || 0) > 0) break;
        await new Promise((r) => setTimeout(r, 400));
      }
      if (cancelled) return;
      if (sawMeshCoordinator) {
        try {
          configureCoordinatorMesh({
            isCoordinator: false,
            coordinatorDeviceId: sawMeshCoordinator.peer_id,
            businessId: businessCodeForLock,
            role: location.pathname.split("/")[1] || null,
          });
          markAskedToday(deviceId, businessCodeForLock);
        } catch (_) { /* noop */ }
        return;
      }
      // Deterministic election-candidate check with ROLE PRIORITY
      // (Session 30.1). Among self + announced mesh peers, only
      // devices of the highest-priority eligible role present
      // (kitchen > bar > staff) enter the lottery; within that set
      // the smallest peer_id proceeds to the claim/prompt path.
      // Everyone else silent-continues. A staff tablet therefore
      // NEVER prompts while a kitchen or bar device is visible on
      // the mesh.
      if (!alreadyClaimedToday) {
        try {
          const ROLE_PRIORITY = { kitchen: 0, bar: 1, staff: 2 };
          const snap = getMeshStatus();
          const announced = Array.isArray(snap?.announcedPeers) ? snap.announcedPeers : [];
          const candidates = [{ peer_id: deviceId, role: currentDashboard }];
          for (const p of announced) {
            if (p && p.peer_id && Object.prototype.hasOwnProperty.call(ROLE_PRIORITY, String(p.role || "").toLowerCase())) {
              candidates.push({ peer_id: p.peer_id, role: String(p.role).toLowerCase() });
            }
          }
          if (candidates.length > 1) {
            const bestPriority = Math.min(...candidates.map((c) => ROLE_PRIORITY[c.role] ?? 9));
            const topTier = candidates
              .filter((c) => (ROLE_PRIORITY[c.role] ?? 9) === bestPriority)
              .map((c) => c.peer_id)
              .sort();
            const elected = topTier[0];
            if (elected !== deviceId) {
              // We're NOT the elected candidate. Silently wait — the
              // leader will claim in the next few seconds and our
              // `configureCoordinatorMesh` will get updated by the
              // subsequent `/coordinator/current` poll (or by the mesh
              // event surface). No prompt on this device.
              markAskedToday(deviceId, businessCodeForLock);
              return;
            }
          }
        } catch (_) { /* mesh introspection best-effort */ }
        // Role-based head start even when the mesh saw no peers:
        // kitchen goes first, bar +1.5s, staff +3s. Keeps two devices
        // that boot simultaneously from racing when WebRTC discovery
        // hasn't completed yet.
        try {
          const extraStagger = currentDashboard === "kitchen" ? 0 : currentDashboard === "bar" ? 1500 : 3000;
          if (extraStagger > 0) await new Promise((r) => setTimeout(r, extraStagger));
          if (cancelled) return;
        } catch (_) { /* noop */ }
      }

      const outcome = await evaluateCoordinatorElection({
        token: activeToken,
        deviceId,
        deviceLabel,
        platform: platformTag,
        businessId: businessCodeForLock,
      });
      if (cancelled) return;
      if (outcome.action === "silent-reclaim") {
        // We were coordinator earlier today — silently resume the
        // heartbeat with no user interruption. This is the common
        // path for "coordinator device took a bathroom break and
        // reopened the app 3 minutes later".
        setIsCoordinator(true);
        // Phase 2 mesh — this device is now the single writer.
        // Inbound leaf-forwarded events start being accepted from
        // the mesh, and our own writes stay on the local queue for
        // the cloud sync loop to drain (not forwarded to anyone).
        try {
          configureCoordinatorMesh({
            isCoordinator: true,
            coordinatorDeviceId: deviceId,
            businessId: outcome.coordinator?.business_id || null,
            role: location.pathname.split("/")[1] || null,
          });
        } catch (_) { /* mesh wiring is best-effort */ }
      } else if (outcome.action === "silent-continue" && outcome.coordinator) {
        // Someone else already holds the role. Register their
        // device_id so `routeWriteThroughCoordinator` knows there IS
        // a coordinator today and can attempt mesh delivery.
        try {
          configureCoordinatorMesh({
            isCoordinator: false,
            coordinatorDeviceId: outcome.coordinator.device_id,
            businessId: outcome.coordinator.business_id || null,
            role: location.pathname.split("/")[1] || null,
          });
        } catch (_) { /* noop */ }
      } else if (outcome.action === "prompt") {
        setShowModal(true);
      }
      // "silent-continue" with no coordinator → mesh routing stays
      // in its default "no coordinator, use cloud" state and every
      // write falls through to the classic cloud sync path.
    })();

    return () => { cancelled = true; };
  }, [isInstalledCtx, dashboardRouteMatch, activeToken, deviceId, deviceLabel, platformTag, location.pathname, rescanTick]);

  // Start / stop the heartbeat loop based on `isCoordinator`. The
  // loop pings `/api/coordinator/heartbeat` every 30s; if the server
  // ever returns 409 (someone else claimed) we drop the role gracefully.
  useEffect(() => {
    if (!isCoordinator || !activeToken || !deviceId) return undefined;
    // Session 27.77 — Offline LAN. As soon as this device becomes the
    // coordinator, publish an mDNS "_nfcs._tcp" service on the local
    // network. Peers running `startLanPeerDiscovery` will pick this
    // up (once we have the Capacitor plugin properly registered on
    // Android + a Tauri equivalent) and can reach us directly over
    // LAN without any cloud dependency. On preview / browser this is
    // a silent no-op (plugin unavailable). No error paths surface to
    // the UI — mDNS is a best-effort enhancement.
    //
    // Session 27.78 — On Tauri (Windows coordinator) we ALSO launch
    // the native HTTP signalling relay on port 8765 so peers can
    // exchange WebRTC SDP over LAN with no cloud dependency.
    let cancelledMdns = false;
    let bootstrapTimer = null; // Session 31.4 — LAN workspace republish timer
    (async () => {
      try {
        const mod = await import("@/lib/mdnsDiscovery");
        if (cancelledMdns) return;
        const inferredRole = (typeof deviceLabel === "string" && deviceLabel.toLowerCase().includes("kitchen")) ? "kitchen"
          : (typeof deviceLabel === "string" && deviceLabel.toLowerCase().includes("bar")) ? "bar"
          : "coordinator";
        await mod.publishSelfService({
          deviceId,
          role: inferredRole,
          deviceLabel: deviceLabel || "Coordinator",
          port: 8765,
        });
        // Also start the native Tauri signalling server (Windows only).
        const sigMod = await import("@/lib/lanSignalling");
        const signaller = sigMod.getSharedSignaller(deviceId);
        await signaller.detect();
        if (signaller.canHostServer()) {
          await signaller.startServerIfHost({
            deviceLabel: deviceLabel || "Coordinator",
            role: inferredRole,
            port: 8765,
          });
        }
        // Session 31.4 — Publish the workspace on the LAN so a brand-new
        // device on this Wi-Fi can copy it and sign in with no internet.
        // Refreshed every 3 minutes (menu / user / table changes).
        const republish = async () => {
          if (cancelledMdns) return;
          try {
            const r = await publishBootstrapFromLocalDb({ deviceLabel: deviceLabel || "Coordinator", role: inferredRole });
            if (r?.published) console.info(`[lan] bootstrap published (${Math.round((r.bytes || 0) / 1024)} KB)`);
          } catch (_) { /* best-effort */ }
        };
        await republish();
        bootstrapTimer = setInterval(republish, 3 * 60 * 1000);
      } catch (_) { /* noop */ }
    })();
    const loop = createHeartbeatLoop({
      token: activeToken,
      deviceId,
      onDethroned: () => {
        // Someone else took over (rare — network partition + double
        // claim). Silently step down; the operator doesn't need a
        // scary toast for this.
        setIsCoordinator(false);
        // Phase 2 mesh — stop accepting inbound coordinator writes
        // and let a subsequent evaluate cycle discover the new
        // coordinator's device_id.
        try {
          configureCoordinatorMesh({ isCoordinator: false, coordinatorDeviceId: null });
        } catch (_) { /* noop */ }
      },
    });
    heartbeatRef.current = loop;
    loop.start();
    return () => {
      cancelledMdns = true;
      try { loop.stop(); } catch (_) { /* noop */ }
      if (bootstrapTimer) { clearInterval(bootstrapTimer); bootstrapTimer = null; }
      // Session 27.77/78 — Retract the mDNS advertisement AND stop
      // the Tauri LAN signalling server when we're no longer
      // coordinator (logout, hand-off, dethroned).
      (async () => {
        try { await unpublishBootstrap(); } catch (_) { /* noop */ }
        try {
          const mod = await import("@/lib/mdnsDiscovery");
          await mod.unpublishSelfService();
        } catch (_) { /* noop */ }
        try {
          const sigMod = await import("@/lib/lanSignalling");
          const signaller = sigMod.getSharedSignaller(deviceId);
          await signaller.stopServerIfHost();
        } catch (_) { /* noop */ }
      })();
      heartbeatRef.current = null;
    };
  }, [isCoordinator, activeToken, deviceId, deviceLabel]);

  // Session 27.64 — Peer coordinator health polling. Every dashboard
  // that is NOT the coordinator polls `/api/coordinator/current` every
  // 20s so it can display a "Coordinator: {label} · {age}" indicator.
  // When the polled record's own last_heartbeat is older than ~90s
  // we tint the pill amber to warn the team the LAN authoritative
  // writer has gone dark — usually a battery-dead tablet or a Wi-Fi
  // dropout on the coordinator's end. On the coordinator device
  // itself this poll is redundant (we already have our own state)
  // so we skip it to save network chatter.
  //
  // Session 27.66 — Also track whether OUR OWN network calls succeed
  // so we don't false-prompt the auto-election when THIS device is
  // the one offline. When `_selfReachable` is false, the coordinator
  // could be perfectly healthy — we just can't see it — so the
  // auto-election path silently no-ops.
  const [selfReachable, setSelfReachable] = useState(true);
  useEffect(() => {
    if (isCoordinator || !isInstalledCtx || !dashboardRouteMatch || !activeToken) {
      setPeerCoordinator(null);
      return undefined;
    }
    let cancelled = false;
    let intervalId = null;
    let consecutiveFailures = 0;
    const tick = async () => {
      // navigator.onLine is a fast preflight — if the OS knows we're
      // disconnected, don't even try. iOS Safari lies about this
      // occasionally, so we treat it as a hint not a hard rule.
      const osOnline = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
      if (!osOnline) {
        if (!cancelled) setSelfReachable(false);
        return;
      }
      try {
        // Session 30.1 — Use the detailed variant so a Wi-Fi-without-
        // internet situation (router up, ISP down) is correctly
        // detected: the old `fetchCurrentCoordinator` swallowed
        // network errors and returned null, which (a) never flipped
        // `selfReachable` to false and (b) CLEARED the last-known
        // coordinator, making leaves forget who to route to the
        // moment the internet died. Now: unreachable → count the
        // failure and KEEP the last-known coordinator so LAN routing
        // stays locked on.
        const probe = await fetchCurrentCoordinatorDetailed(activeToken);
        if (cancelled) return;
        if (probe.reachable) {
          consecutiveFailures = 0;
          setSelfReachable(true);
          setPeerCoordinator(probe.coordinator || null);
        } else {
          consecutiveFailures += 1;
          if (consecutiveFailures >= 2) {
            setSelfReachable(false);
          }
        }
      } catch (_) {
        // Network / server failure. Track how many polls in a row have
        // failed so a single flaky request doesn't flip us to "offline"
        // instantly. Two failures = ~40s of confirmed unreachability.
        consecutiveFailures += 1;
        if (!cancelled && consecutiveFailures >= 2) {
          setSelfReachable(false);
        }
      }
    };
    tick();
    intervalId = setInterval(tick, 20 * 1000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isCoordinator, isInstalledCtx, dashboardRouteMatch, activeToken]);

  // Session 27.76 — Expose the current coordinator's device_id via a
  // global helper so the MeshStatusPill popover can render a small
  // crown icon on the peer row that matches. The helper reads the
  // coordinator state whether we hold it locally (`isCoordinator`)
  // or a peer holds it (`peerCoordinator` from `/current`).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__nfcsGetCoordinatorDeviceId = () => {
      try {
        if (isCoordinator && deviceId) return deviceId;
        if (peerCoordinator && peerCoordinator.device_id) return peerCoordinator.device_id;
        return null;
      } catch (_) { return null; }
    };
    return () => {
      try { delete window.__nfcsGetCoordinatorDeviceId; } catch (_) { /* noop */ }
    };
  }, [isCoordinator, peerCoordinator, deviceId]);

  // 1-second re-render tick so the "45s ago" age string in the peer
  // pill stays fresh without waiting for the next poll cycle. Cheap:
  // only fires while a peer pill is actually visible.
  useEffect(() => {
    if (isCoordinator || !peerCoordinator) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isCoordinator, peerCoordinator]);

  // Session 27.65/27.66 — Coordinator disconnect auto-election.
  //
  // When the peer coordinator's heartbeat is more than 90 seconds
  // old, we treat that device as offline (dead battery, closed app,
  // Wi-Fi dropped). Every non-coordinator dashboard automatically
  // opens the "Is this device the Coordinator?" modal so whoever has
  // the backup tablet can tap Yes and take over.
  //
  // CRITICAL: 27.66 — We ONLY trigger this if `selfReachable` is
  // true, i.e. THIS device is still reaching the cloud. If our own
  // requests are failing, the coordinator might be perfectly fine —
  // it's WE who lost the network, and we must NOT ask the whole
  // restaurant to elect a new coordinator just because our tablet's
  // Wi-Fi hiccuped. Offline-first: assume the local network is the
  // source of truth for reachability, and prefer to do nothing over
  // flipping the wrong coordinator.
  //
  // The `_promptedForDeviceId` ref prevents the modal from re-opening
  // every 20s once dismissed and prevents multiple prompts for the
  // same dead coordinator.
  const _promptedForDeviceRef = useRef(null);
  useEffect(() => {
    if (isCoordinator || !peerCoordinator || !dashboardRouteMatch || !isInstalledCtx) return;
    // 27.66 — hard gate: if we can't reach the cloud ourselves,
    // we CAN'T reliably conclude the coordinator is offline. Skip.
    if (!selfReachable) return;
    let ageMs = 0;
    try {
      const hb = peerCoordinator.last_heartbeat
        ? new Date(peerCoordinator.last_heartbeat).getTime()
        : 0;
      ageMs = hb ? Math.max(0, nowTick - hb) : 0;
    } catch (_) { ageMs = 0; }
    const veryStale = ageMs > 90 * 1000; // Session 27.66: 90s offline threshold (was 3min)
    if (!veryStale) return;
    // Session 30.1 — Role-priority takeover (operator spec: if the
    // coordinator dies, Kitchen is asked first; if Kitchen isn't
    // around, Bar; then Staff). Implemented as a staggered prompt:
    // kitchen prompts at 90s stale, bar at 100s, staff at 110s. The
    // 2s modal-poll (Session 27.88) auto-dismisses the later prompts
    // as soon as an earlier device claims. Non-eligible dashboards
    // (owner, runner, admin, retail) never prompt.
    const takeoverRoute = (location.pathname.split("/")[1] || "").toLowerCase();
    const TAKEOVER_DELAY_MS = { kitchen: 0, bar: 10 * 1000, staff: 20 * 1000 };
    if (!Object.prototype.hasOwnProperty.call(TAKEOVER_DELAY_MS, takeoverRoute)) return;
    if (ageMs < (90 * 1000 + TAKEOVER_DELAY_MS[takeoverRoute])) return;
    const staleKey = peerCoordinator.device_id || peerCoordinator.last_heartbeat || "unknown";
    if (_promptedForDeviceRef.current === staleKey) return;
    _promptedForDeviceRef.current = staleKey;
    // Show the election modal AND announce it via a toast so any
    // staff nearby knows what's happening. The modal itself is the
    // interactive path — tapping Yes claims the role for THIS device.
    setShowModal(true);
    try {
      toast.warning("Coordinator offline — asking every device to elect a new one.", {
        description: `${peerCoordinator.device_label || staleKey} hasn't checked in for over 90 seconds.`,
        duration: 8000,
      });
    } catch (_) { /* toast is best-effort */ }
  }, [isCoordinator, peerCoordinator, nowTick, dashboardRouteMatch, isInstalledCtx, selfReachable, location.pathname]);
  // Reset the "already prompted" ref when a healthy coordinator
  // returns, so the next disconnect can prompt fresh.
  useEffect(() => {
    if (!peerCoordinator) return;
    let ageMs = 0;
    try {
      const hb = peerCoordinator.last_heartbeat
        ? new Date(peerCoordinator.last_heartbeat).getTime()
        : 0;
      ageMs = hb ? Math.max(0, Date.now() - hb) : 0;
    } catch (_) { ageMs = 0; }
    if (ageMs < 30 * 1000) {
      _promptedForDeviceRef.current = null;
    }
  }, [peerCoordinator]);

  // Session 27.88 — Auto-dismiss the election modal if a peer device
  // claims the Coordinator role while WE are still looking at the
  // "Is this device the Coordinator?" prompt.
  //
  // Bug this fixes: three tablets all sign in within the same second.
  // They each run `evaluateCoordinatorElection`, each fall through the
  // 3.5s discovery window without seeing a claim, each auto-claim +
  // release, and each opens the prompt modal. Whichever operator taps
  // Yes first becomes the coordinator — but the other two modals stay
  // open indefinitely, prompting the operator to "pick a coordinator"
  // even though one has already been picked on a sibling device. The
  // operator taps Skip / Yes / dismisses, but the same modal cascade
  // fires again the next time any of those tablets refreshes.
  //
  // Fix: while `showModal` is true, poll `/api/coordinator/current`
  // every 2 s. If a real, alive coordinator materialises whose
  // device_id is NOT us, close the modal, mark "asked today" so we
  // don't re-prompt on this device, and wire up the mesh to route
  // through the peer coordinator. UX net effect: whichever tablet
  // says Yes first "wins", and the other modals silently disappear
  // within a couple of seconds.
  useEffect(() => {
    if (!showModal) return undefined;
    if (!activeToken || !deviceId) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      // Session 27.94 — Primary signal: did a peer just broadcast a
      // `coordinator_beacon` we can trust? If yes, silently step
      // down without needing the backend round-trip. Falls back to
      // the announced-peers check (for older peers that don't
      // beacon yet) and finally the backend `/coordinator/current`.
      try {
        const beacon = lastSeenCoordinatorRef.current || { device_id: null, at: 0 };
        if (beacon.device_id && beacon.device_id !== deviceId
            && (Date.now() - beacon.at) < 60000) {
          try {
            const bizId = window.localStorage.getItem("nfcs.business_code") || null;
            markAskedToday(deviceId, bizId);
          } catch (_) { /* noop */ }
          try {
            configureCoordinatorMesh({
              isCoordinator: false,
              coordinatorDeviceId: beacon.device_id,
              businessId: null,
              role: location.pathname.split("/")[1] || null,
            });
          } catch (_) { /* noop */ }
          setShowModal(false);
          return;
        }
      } catch (_) { /* noop */ }
      try {
        const snap = getMeshStatus();
        const coordPeer = Array.isArray(snap?.announcedPeers)
          ? snap.announcedPeers.find((p) => p && p.role === "coordinator" && p.peer_id && p.peer_id !== deviceId)
          : null;
        if (coordPeer) {
          try {
            const bizId = window.localStorage.getItem("nfcs.business_code") || null;
            markAskedToday(deviceId, bizId);
          } catch (_) { /* noop */ }
          try {
            configureCoordinatorMesh({
              isCoordinator: false,
              coordinatorDeviceId: coordPeer.peer_id,
              businessId: null,
              role: location.pathname.split("/")[1] || null,
            });
          } catch (_) { /* noop */ }
          setShowModal(false);
          return;
        }
      } catch (_) { /* mesh best-effort */ }
      let current = null;
      try { current = await fetchCurrentCoordinator(activeToken); }
      catch (_) { current = null; }
      if (cancelled) return;
      if (current && current.device_id && current.device_id !== deviceId) {
        // A peer got there first — silently step down.
        try {
          const bizId = window.localStorage.getItem("nfcs.business_code") || null;
          markAskedToday(deviceId, bizId);
        } catch (_) { /* noop */ }
        try {
          configureCoordinatorMesh({
            isCoordinator: false,
            coordinatorDeviceId: current.device_id || null,
            businessId: current.business_id || null,
            role: location.pathname.split("/")[1] || null,
          });
        } catch (_) { /* noop */ }
        setShowModal(false);
      }
    };
    // First check quickly (400ms) so if the race resolved before the
    // modal even rendered we skip flicker.
    const t0 = window.setTimeout(tick, 400);
    const t1 = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(t0);
      window.clearInterval(t1);
    };
  }, [showModal, activeToken, deviceId, location.pathname]);

  const handleYes = useCallback(async () => {
    if (!activeToken || !deviceId || submitting || isCoordinator) return;
    setSubmitting(true);
    try {
      // Session 27.69 — Race protection: before firing our own claim,
      // do a quick server-side check to see if another device already
      // won the election. When two backup tablets both see the same
      // "Coordinator offline" modal at the same moment and both tap
      // Yes, the pre-check saves the loser from ever seeing the ugly
      // "Coordinator claim failed" toast — we short-circuit with a
      // gentle "Another device took over" message instead. The
      // backend's uniqueness constraint still protects the DB, so
      // this is UX-only defence in depth.
      try {
        const already = await fetchCurrentCoordinator(activeToken);
        if (already && already.device_id && already.device_id !== deviceId) {
          setShowModal(false);
          toast.message("Another device already became Coordinator.");
          try {
            configureCoordinatorMesh({
              isCoordinator: false,
              coordinatorDeviceId: already.device_id || null,
              businessId: already.business_id || null,
              role: location.pathname.split("/")[1] || null,
            });
          } catch (_) { /* noop */ }
          return;
        }
      } catch (_) { /* pre-check is best-effort — proceed on failure */ }
      let r;
      try {
        r = await claimCoordinator({
          token: activeToken,
          deviceId,
          deviceLabel,
          platform: platformTag,
        });
      } catch (thrown) {
        // Session 27.62 — claimCoordinator is designed to never throw
        // but be defensive: any unexpected exception surfaces here
        // with the reason so the operator isn't left staring at a
        // silent "nothing happened" state.
        // eslint-disable-next-line no-console
        console.warn("[coordinator/claim] threw unexpectedly:", thrown);
        toast.error(`Coordinator claim failed — ${thrown?.message || "unknown error"}. Please try again.`);
        return;
      }
      if (r.status === "error" && (!r.httpStatus || r.httpStatus === 0)) {
        // Session 31.4 — Cloud unreachable (Wi-Fi without internet). The
        // LAN probe found no coordinator, so take the role LOCALLY: LAN
        // server + mDNS + workspace publishing start right away and the
        // heartbeat loop registers the claim with the cloud the moment
        // internet returns (server auto-registers when nobody holds it).
        setIsCoordinator(true);
        setShowModal(false);
        try {
          const bizId = window.localStorage.getItem("nfcs.business_code") || null;
          markAskedToday(deviceId, bizId);
          markClaimedToday(deviceId, bizId);
        } catch (_) { /* noop */ }
        toast.success("This device is the Coordinator (offline)", { description: "Other devices on this Wi-Fi will find it. The cloud is updated automatically when internet returns." });
        try { playCoordinatorHandoverChime(); } catch (_) { /* noop */ }
        try {
          configureCoordinatorMesh({ isCoordinator: true, coordinatorDeviceId: deviceId, businessId: null, role: location.pathname.split("/")[1] || null });
        } catch (_) { /* noop */ }
        return;
      }
      if (r.status === "claimed" || r.status === "silent_reclaim" || r.status === "already_yours") {
        // Session 27.80 — Belt-and-braces re-verify. Even after the
        // backend told us we won, wait 300ms and re-check /current.
        // If a concurrent claim landed slightly earlier and Mongo's
        // reconciliation crowned someone else, we release our claim
        // and stay a leaf. This is the LAST defense against the
        // "all three tablets show themselves as coordinator" bug.
        let confirmedCoordinator = true;
        try {
          await new Promise((r0) => setTimeout(r0, 300));
          const verify = await fetchCurrentCoordinator(activeToken);
          if (verify && verify.device_id && verify.device_id !== deviceId) {
            confirmedCoordinator = false;
          }
        } catch (e) { console.warn("[coordinator] verify after claim failed:", e?.message || e); }
        if (!confirmedCoordinator) {
          setIsCoordinator(false);
          setShowModal(false);
          toast.message("Another device won the race — this device stays as a leaf.");
          return;
        }
        setIsCoordinator(true);
        setShowModal(false);
        // Session 27.72 — Lock the "asked today" flag so a subsequent
        // sign-in on this same device today doesn't re-prompt even if
        // the coordinator record is ever cleared server-side (e.g. a
        // manual DB fix during a support call).
        // Session 30.1 — ALSO stamp the per-day "claimed today" memory
        // so role switches / refreshes / offline stints silently
        // resume the role instead of ever re-prompting.
        try {
          const bizId = window.localStorage.getItem("nfcs.business_code") || null;
          markAskedToday(deviceId, bizId);
          markClaimedToday(deviceId, bizId);
        } catch (_) { /* noop */ }
        toast.success("This device is today's Coordinator.");
        try { playCoordinatorHandoverChime(); } catch (_) { /* noop */ }
        // Phase 2 mesh — this device becomes the authoritative
        // writer for the day. Enable inbound handling so leaf
        // devices can forward their orders here via WebRTC.
        try {
          configureCoordinatorMesh({
            isCoordinator: true,
            coordinatorDeviceId: deviceId,
            businessId: r.coordinator?.business_id || null,
            role: location.pathname.split("/")[1] || null,
          });
        } catch (_) { /* mesh wiring is best-effort */ }
      } else if (r.status === "taken") {
        setShowModal(false);
        // Session 27.80 — Defensive: force local coordinator state
        // to false so a stale UI doesn't keep showing this device as
        // coordinator when the backend just told us someone else won.
        // Also mark asked-today so we don't re-prompt on refresh.
        setIsCoordinator(false);
        try {
          const bizId = window.localStorage.getItem("nfcs.business_code") || null;
          markAskedToday(deviceId, bizId);
        } catch (_) { /* noop */ }
        toast.message("Another device already became Coordinator.");
        // Register whoever holds the role so this leaf device can
        // forward writes to them via mesh.
        try {
          configureCoordinatorMesh({
            isCoordinator: false,
            coordinatorDeviceId: r.coordinator?.device_id || null,
            businessId: r.coordinator?.business_id || null,
            role: location.pathname.split("/")[1] || null,
          });
        } catch (_) { /* noop */ }
      } else {
        // Session 27.62 — Surface the actual failure reason. Previous
        // "Please try again" left operators debugging blind on
        // valid-network scenarios (e.g. token expired mid-session,
        // Cloudflare returned 522, backend rolled a fresh session
        // context error). The detail is now human-readable and
        // includes the HTTP status when the server actually responded.
        const httpStatus = r.httpStatus || 0;
        const detail = r.detail || "";
        const prefix = httpStatus ? `HTTP ${httpStatus}` : "Network error";
        const message = detail
          ? `Coordinator claim failed (${prefix}: ${detail}). Please try again.`
          : `Coordinator claim failed (${prefix}). Please try again.`;
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  }, [activeToken, deviceId, deviceLabel, platformTag, submitting, location.pathname, isCoordinator]);

  const handleSkip = useCallback(async () => {
    if (!activeToken || !deviceId || submitting) return;
    setSubmitting(true);
    try {
      await skipCoordinatorElection({ token: activeToken, deviceId });
      // Session 27.72 — Also lock the local "asked today" flag so a
      // subsequent sign-in on this device today respects the Skip
      // decision even if the server-side skip record is delayed.
      try {
        const bizId = window.localStorage.getItem("nfcs.business_code") || null;
        markAskedToday(deviceId, bizId);
      } catch (_) { /* noop */ }
      // Session 27.78 — Broadcast a "coordinator vacated / skip" event
      // over the mesh so other devices immediately re-evaluate the
      // election gate. Previously, when this operator picked "No, move
      // to a different device", the other devices had already gone
      // silent-continue during their initial poll and never got a
      // chance to prompt. Now every peer that receives this event
      // clears its "asked today" lock and re-runs `evaluate`.
      try {
        fireAndForgetMesh({
          type: "coordinator_vacated",
          from_device_id: deviceId,
          at: new Date().toISOString(),
        }, { targetRole: "*" });
      } catch (_) { /* noop */ }
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  }, [activeToken, deviceId, submitting]);

  // Session 27.63 — Voluntary hand-off. Called from the pill's
  // dropdown ("Hand off Coordinator") on the device currently
  // holding the role. Releases the server-side coordinator record
  // (server keeps a 20-minute silent-reclaim window so a mistap
  // isn't catastrophic) and drops mesh routing so subsequent writes
  // route through the next elected device. Any dashboard role can
  // hand off — the button lives on the Coordinator pill itself, so
  // whoever holds the role can release it, regardless of whether
  // that device is Staff, Owner, Kitchen or Bar.
  const handleHandoff = useCallback(async () => {
    if (!activeToken || !deviceId || handingOff) return;
    setHandingOff(true);
    try {
      // Session 27.64 — arm the cooldown FIRST so even if the network
      // call below races or fails, this device won't auto-reclaim on
      // the next election evaluation. Cleared automatically after 60s.
      try { markHandoffCooldown(deviceId, 60 * 1000); } catch (_) { /* noop */ }
      // Session 30.1 — Explicit hand-off is the ONLY thing that clears
      // the per-day "claimed today" memory. Without this, the device
      // would silently reclaim the role on its next evaluate cycle.
      try {
        const bizId = window.localStorage.getItem("nfcs.business_code") || null;
        clearClaimedToday(deviceId, bizId);
      } catch (_) { /* noop */ }
      const ok = await releaseCoordinator({ token: activeToken, deviceId });
      // Optimistically drop the role locally either way — the
      // heartbeat loop's next tick would drop it anyway on 409.
      setIsCoordinator(false);
      try {
        configureCoordinatorMesh({ isCoordinator: false, coordinatorDeviceId: null });
      } catch (_) { /* noop */ }
      // Stop the heartbeat immediately so we don't reclaim the role
      // in the next tick.
      try { heartbeatRef.current?.stop(); } catch (_) { /* noop */ }
      heartbeatRef.current = null;
      setHandoffOpen(false);
      if (ok) {
        toast.success("Coordinator released. Another device will be asked on the next sign-in.");
        try { playCoordinatorHandoverChime(); } catch (_) { /* noop */ }
      } else {
        // Server reported not-ok — the role may have already
        // expired or been claimed elsewhere. Not a hard failure
        // from the user's perspective.
        toast.message("Coordinator role released locally.");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[coordinator/release] failed:", err);
      toast.error(`Hand-off failed — ${err?.message || "unknown error"}. Please try again.`);
    } finally {
      setHandingOff(false);
    }
  }, [activeToken, deviceId, handingOff]);

  // Nothing to render when the modal isn't open AND we don't need a
  // status badge. (A future enhancement: a small "🟢 Coordinator"
  // pill in the header for the coordinator device — deliberately
  // deferred until Phase 2 wires up the mesh, so the label has real
  // meaning.)
  // Nothing to render outside installed contexts (browser tabs stay
  // strictly cloud-first — no election prompt, no coordinator badge).
  if (!isInstalledCtx) return null;

  // Session 27.58 — "Coordinator" pill in the header of the elected
  // device. Rendered as a fixed-position element outside any layout
  // so it lives on top of every dashboard's own header, and never
  // fights the theme toggle / waiter-call / settings buttons for
  // space. Only paints on the device that actually holds the role;
  // leaf devices render nothing, keeping the UI clutter-free.
  //
  // Positioned bottom-left rather than top-right because the top-
  // right of every dashboard is dense with the sign-out / theme /
  // actions menu — bottom-left is empty on all five dashboards and
  // sits well above the FloatingSettingsButton (top-right on
  // installed contexts, so no overlap). Also ~always above the
  // Android soft-keyboard, so it never gets pushed off-screen while
  // the operator is typing an order note.
  return (
    <>
      {isCoordinator ? (
        <div
          className="fixed bottom-4 left-4 z-[250]"
          data-testid="coordinator-badge-wrapper"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm backdrop-blur-md transition-colors hover:bg-emerald-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 dark:border-emerald-400/30 dark:bg-emerald-400/15 dark:text-emerald-300 dark:hover:bg-emerald-400/25"
                title="This device is holding today's operational data. Tap for options."
                data-testid="coordinator-badge"
                aria-label="Coordinator options"
              >
                <span
                  className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-emerald-500 dark:bg-emerald-400"
                  aria-hidden="true"
                />
                Coordinator
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              className="w-64"
              data-testid="coordinator-badge-menu"
            >
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                This device is today's Coordinator. All orders, tips and
                payments flow through here before syncing to the cloud.
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); setHandoffOpen(true); }}
                className="text-red-600 focus:text-red-600 focus:bg-red-500/10 dark:text-red-400 dark:focus:text-red-400"
                data-testid="coordinator-handoff-menu-item"
              >
                Hand off Coordinator…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog open={handoffOpen} onOpenChange={setHandoffOpen}>
            <AlertDialogContent data-testid="coordinator-handoff-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="coordinator-handoff-title">
                  Hand off Coordinator role?
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="coordinator-handoff-description">
                  This device will stop being the authoritative writer for
                  today's shift. The next device that signs in on this
                  restaurant's network will be asked to take over. If nobody
                  takes over within a few minutes, this same device can
                  reclaim the role silently on its next sign-in (20-minute
                  grace window).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  disabled={handingOff}
                  data-testid="coordinator-handoff-cancel-button"
                >
                  Cancel
                </AlertDialogCancel>
                <Button
                  onClick={handleHandoff}
                  disabled={handingOff}
                  variant="destructive"
                  data-testid="coordinator-handoff-confirm-button"
                >
                  {handingOff ? "Handing off…" : "Yes, hand off"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : null}
      {/* Session 27.64 — Peer coordinator health pill.
          Rendered on NON-coordinator dashboards when we have a
          reachable coordinator record. Shows device label +
          heartbeat age, tinted amber past ~90s stale. Cheap
          glanceable LAN-health check for staff on any device. */}
      {!isCoordinator && peerCoordinator && dashboardRouteMatch && isInstalledCtx ? (
        (() => {
          let ageMs = 0;
          try {
            const hb = peerCoordinator.last_heartbeat
              ? new Date(peerCoordinator.last_heartbeat).getTime()
              : 0;
            ageMs = hb ? Math.max(0, nowTick - hb) : 0;
          } catch (_) { ageMs = 0; }
          // Session 27.66 — Faster thresholds so a real disconnect
          // is surfaced within ~90 seconds, but a 30-second WiFi
          // hiccup still recovers silently. Also added a "we're the
          // one offline" state (grey, non-clickable, informational)
          // so we don't imply the coordinator is down when it's
          // actually THIS device that lost the network.
          const stale = ageMs > 45 * 1000;
          const veryStale = ageMs > 90 * 1000;
          const weAreOffline = !selfReachable;
          const ageLabel =
            ageMs < 10 * 1000 ? "just now" :
            ageMs < 60 * 1000 ? `${Math.round(ageMs / 1000)}s ago` :
            ageMs < 60 * 60 * 1000 ? `${Math.round(ageMs / 60000)}m ago` :
            `${Math.round(ageMs / 3600000)}h ago`;
          // "We are offline" takes priority over "coordinator stale"
          // — the coordinator might be totally fine.
          const tone = weAreOffline
            ? "border-slate-400/40 bg-slate-500/10 text-slate-600 dark:border-slate-500/40 dark:bg-slate-500/10 dark:text-slate-400"
            : veryStale
              ? "border-red-500/30 bg-red-500/15 text-red-700 dark:border-red-400/30 dark:bg-red-400/15 dark:text-red-300"
              : stale
                ? "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/15 dark:text-amber-300"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300";
          const dot = weAreOffline
            ? "bg-slate-400 dark:bg-slate-500"
            : veryStale
              ? "bg-red-500 dark:bg-red-400"
              : stale
                ? "bg-amber-500 dark:bg-amber-400"
                : "bg-emerald-500 dark:bg-emerald-400";
          const label = peerCoordinator.device_label || peerCoordinator.device_id || "Unknown device";
          const trunc = label.length > 18 ? `${label.slice(0, 16)}…` : label;
          let text;
          let title;
          if (weAreOffline) {
            text = (
              <>
                <span>This device is offline</span>
                <span className="opacity-70">· Working locally</span>
              </>
            );
            title = "This device can't reach the internet. The app keeps working offline — orders, tips and payments are queued and will sync automatically when Wi-Fi returns. We're not showing coordinator status because we can't reliably know if the coordinator is still up.";
          } else if (veryStale) {
            text = (
              <>
                <span>Coordinator offline</span>
                <span className="opacity-70">· Tap to take over</span>
              </>
            );
            title = `${label} hasn't checked in for ${ageLabel}. The device may have died, lost Wi-Fi, or been closed. Tap this pill to become the new Coordinator so orders keep flowing.`;
          } else if (stale) {
            text = (
              <>
                <span>Coordinator lagging</span>
                <span className="opacity-70">· {ageLabel}</span>
              </>
            );
            title = `${label} is still online but its last heartbeat was ${ageLabel}. If this doesn't recover in the next minute, other devices will be asked to take over.`;
          } else {
            text = (
              <>
                <span className="opacity-70">Connected to</span>
                <span data-testid="coordinator-peer-label">{trunc}</span>
              </>
            );
            title = `Today's Coordinator: ${label}. Last heartbeat ${ageLabel}. Orders, tips and payments from this device are being routed through ${label} for consistency.`;
          }
          const clickable = veryStale && !weAreOffline;
          const commonClass = `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium shadow-sm backdrop-blur-md transition-colors ${tone}`;
          const inner = (
            <>
              <span
                className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dot}${!stale && !weAreOffline ? " animate-pulse" : ""}${veryStale && !weAreOffline ? " animate-pulse" : ""}`}
                aria-hidden="true"
              />
              {text}
              <span className="sr-only" data-testid="coordinator-peer-age">{ageLabel}</span>
            </>
          );
          return (
            <div
              className={`fixed bottom-4 left-4 z-[249] ${clickable ? "" : "pointer-events-none"}`}
              data-testid="coordinator-peer-badge-wrapper"
            >
              {clickable ? (
                <button
                  type="button"
                  className={`${commonClass} pointer-events-auto cursor-pointer hover:bg-red-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50`}
                  onClick={() => setShowModal(true)}
                  title={title}
                  data-testid="coordinator-peer-badge"
                  aria-label="Coordinator offline. Tap to take over."
                >
                  {inner}
                </button>
              ) : (
                <span
                  className={commonClass}
                  title={title}
                  data-testid="coordinator-peer-badge"
                >
                  {inner}
                </span>
              )}
            </div>
          );
        })()
      ) : null}
      {showModal ? (
        <Dialog open={showModal} onOpenChange={(open) => { if (!open) handleSkip(); }}>
          <DialogContent
            className="w-[calc(100vw-2rem)] max-w-md max-h-[90vh] overflow-y-auto rounded-2xl"
            data-testid="coordinator-election-modal"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            {/* Session 27.68 — Layout fix: was `max-w-sm` (384px) with
                no vertical overflow guard. On landscape tablets the
                description (~5 lines) overflowed the ~600px viewport
                height and the Yes/Skip buttons got clipped below the
                fold. Now the dialog respects the viewport (`w-` calc
                for safe horizontal padding) AND scrolls vertically
                (`max-h-[90vh] overflow-y-auto`) so buttons always
                reach. */}
            <DialogHeader>
              <DialogTitle data-testid="coordinator-election-title">
                Is this device the Coordinator for today?
              </DialogTitle>
              <DialogDescription data-testid="coordinator-election-description">
                The Coordinator collects orders, payments and updates from
                every other device on this restaurant's network and keeps
                them safely on this device — then syncs to the cloud in
                the background. Pick the device closest to your WiFi
                router. This role holds for the rest of today; if you Skip,
                the next device to log in will be asked.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={submitting}
                data-testid="coordinator-election-skip-button"
              >
                Skip — ask another device
              </Button>
              <Button
                onClick={handleYes}
                disabled={submitting || isCoordinator}
                data-testid="coordinator-election-yes-button"
              >
                {submitting ? "Claiming…" : "Yes, use this device"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

// Order-ready chime — fires on the Staff dashboard when kitchen or bar
// marks a ticket as ready to serve.
// ---- Clean notification sound engine (HTML5 Audio, WAV-based) ----
// Generates clean sine-wave WAV audio as data URIs. Used by ALL dashboards.
const _cleanNotifSounds = (() => {
  function makeWav(samples, sampleRate) {
    const numChannels = 1, bitsPerSample = 16;
    const byteRate = sampleRate * numChannels * (bitsPerSample / 8);
    const blockAlign = numChannels * (bitsPerSample / 8);
    const dataSize = samples.length * (bitsPerSample / 8);
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const ws = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
    ws(0,'RIFF'); view.setUint32(4,36+dataSize,true); ws(8,'WAVE'); ws(12,'fmt ');
    view.setUint32(16,16,true); view.setUint16(20,1,true); view.setUint16(22,numChannels,true);
    view.setUint32(24,sampleRate,true); view.setUint32(28,byteRate,true);
    view.setUint16(32,blockAlign,true); view.setUint16(34,bitsPerSample,true);
    ws(36,'data'); view.setUint32(40,dataSize,true);
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    const bytes = new Uint8Array(buffer);
    let b = ''; for (let i = 0; i < bytes.length; i++) b += String.fromCharCode(bytes[i]);
    return 'data:audio/wav;base64,' + btoa(b);
  }
  function env(t, atk, dec, total) {
    if (t < atk) return t / atk;
    const rem = total - t;
    if (rem < dec) return Math.max(0, rem / dec);
    return 1;
  }
  // Warm Chord: C5+E5+G5 major chord, 600ms
  function warmChord() {
    const sr=22050, dur=0.6, n=Math.floor(sr*dur), out=new Float32Array(n);
    for(let i=0;i<n;i++){const t=i/sr,e=env(t,0.01,0.3,dur);out[i]=e*0.28*(Math.sin(2*Math.PI*523.25*t)+Math.sin(2*Math.PI*659.25*t)+Math.sin(2*Math.PI*783.99*t));}
    return makeWav(out, sr);
  }
  // Bright Bell: Rising A5→D6
  function brightBell() {
    const sr=22050, dur=0.5, n=Math.floor(sr*dur), out=new Float32Array(n);
    for(let i=0;i<n;i++){const t=i/sr,e=env(t,0.005,0.25,dur),f=t<0.2?880:1174.66;out[i]=e*0.35*(Math.sin(2*Math.PI*f*t)+0.3*Math.sin(2*Math.PI*f*2*t));}
    return makeWav(out, sr);
  }
  // Mellow Marimba: F5 with fast exponential decay
  function mellowMarimba() {
    const sr=22050, dur=0.45, n=Math.floor(sr*dur), out=new Float32Array(n);
    for(let i=0;i<n;i++){const t=i/sr,e=Math.exp(-6*t);out[i]=e*0.4*(Math.sin(2*Math.PI*698.46*t)+0.5*Math.sin(2*Math.PI*698.46*4*t)*Math.exp(-12*t));}
    return makeWav(out, sr);
  }
  // Digital Chirp: Two ascending beeps B5→E6
  function digitalChirp() {
    const sr=22050, dur=0.35, n=Math.floor(sr*dur), out=new Float32Array(n);
    for(let i=0;i<n;i++){const t=i/sr,is2=t>0.15,lt=is2?t-0.15:t,f=is2?1318.51:987.77,ld=is2?0.2:0.12,e=env(lt,0.005,0.08,ld);out[i]=e*0.4*Math.sin(2*Math.PI*f*t);}
    return makeWav(out, sr);
  }
  // Subtle ping for confirmations (quiet, short)
  function subtlePing() {
    const sr=22050, dur=0.25, n=Math.floor(sr*dur), out=new Float32Array(n);
    for(let i=0;i<n;i++){const t=i/sr,e=env(t,0.005,0.12,dur);out[i]=e*0.2*Math.sin(2*Math.PI*880*t);}
    return makeWav(out, sr);
  }
  const _cache = {};
  function _gen(key, fn) { if (!_cache[key]) _cache[key] = fn(); return _cache[key]; }
  return {
    play(presetId, volume) {
      try {
        let src;
        switch(presetId) {
          case 'warm_chord': src = _gen('wc', warmChord); break;
          case 'bright_bell': src = _gen('bb', brightBell); break;
          case 'mellow_marimba': src = _gen('mm', mellowMarimba); break;
          case 'subtle_ping': src = _gen('sp', subtlePing); break;
          default: src = _gen('dc', digitalChirp); break;
        }
        const a = new Audio(src);
        a.volume = volume ?? 0.7;
        a.play().catch(() => {});
      } catch(_) {}
    }
  };
})();

// Coordinator hand-off confirmation — very short, gentle descending
// sweep. Only fires on the ONE device that just handed off / picked
// up the role, and only on the confirm click — never in a loop. Kept
// low-volume so it's a subtle "acknowledged" cue, not a demand for
// attention. No vibration.
function playCoordinatorHandoverChime() {
  _cleanNotifSounds.play("subtle_ping", 0.3);
}
